/**
 * The offline-attendance sync engine (`specs/offline-attendance-v1/implementation-plan.md`,
 * task FE-8; decisions D11, D12, D13; plan amendments C15, C16; Review Focus
 * 1, 2 and 3).
 *
 * ONE engine per scope and session uploads the scope's ordered chain of
 * captured punches to `POST /v1/api/me/attendance/sync` and turns ERPat's
 * per-event answers into settlements — exactly once, whatever happens to the
 * network, the app or the session in between:
 *
 *   - Single flight: `run()` / `kick()` never start a second pass while one is
 *     running; a request made meanwhile sets a re-run flag, so a punch captured
 *     during an upload is sent in the next pass without waiting for a timer.
 *   - One owner per scope (D11): every pass holds the scope's IndexedDB lease
 *     (renewed while a request is out) and every write it makes is fenced by
 *     the lease generation. A live lease of another owner (another tab) means
 *     no request; a lease lost mid-pass ends the pass QUIETLY — no settlement,
 *     no pause — and whoever holds it next recovers the rows and resends them
 *     (C15). The lease is given up at the end of every pass.
 *   - Settlement is strict (D12): only `settleOutcome()` decides, and HTTP 200
 *     never implies acceptance. Receipts are sealed BEFORE the settlement
 *     transaction and stored in it; a row is final only together with the
 *     receipt that settled it.
 *   - An unknown outcome is resent with the same `client_event_id` and
 *     `device_seq` (Review Focus 1): a transport failure, an unusable or
 *     unexplained answer, or a settlement that could not be stored puts the
 *     rows back to `pending`; a killed process leaves them `in_flight`, and the
 *     next owner recovers them. ERPat answers the resend `duplicate` with the
 *     original receipt.
 *   - Nothing outlives its session (Review Focus 3): `stop()` bumps the
 *     generation, aborts the request and releases the lease; every await is
 *     followed by a check of the generation AND of `isCurrent()` (the
 *     orchestrator's session epoch + ownership), so a response that arrives
 *     after a sign-out or a company switch settles nothing.
 *   - Back-off never gives up on a valid event: 5 s initial, 60 s cap, jittered
 *     (`syncBackoffMs`), no attempt cap; `Retry-After` and per-event
 *     `retry_after_ms` are honoured but bounded at one hour (C16). Auth,
 *     permission, protocol and local-storage failures PAUSE the engine instead;
 *     a pause is re-checked every 15 minutes, skipped by a forced run (Sync
 *     now), and cleared by `resume()`.
 *   - A clock turned back never holds the queue. Retry times, pauses and lease
 *     expiries are device WALL-clock times; one further ahead than the longest
 *     wait the engine could have scheduled (`clockTurnedBack`: a retry hint
 *     plus the back-off ceiling, 15 minutes, a lease's 30 s) was written before
 *     the clock was corrected backwards, and counts as over — else the queue
 *     would wait for as long as the clock moved, and past the upload age ERPat
 *     would refuse the punches as `too_old`. A punch never sent is always due.
 *     A wait within the bound, the server's Retry-After included, is kept.
 *
 * Only `postSync` is a dependency; everything else is the repository
 * (`attendance-storage.ts`), which is the single gateway to IndexedDB.
 */
import {
  DEFERRAL_CODES,
  OFFLINE_SCHEMA_VERSION,
  WHOLE_REQUEST_ERROR_CODES,
  decodeResult,
  decodeSyncResponse,
  normalizeUuid,
  type AttendanceResultV1,
  type EmployeeAttendanceEventV1,
  type FinalReceiptV1,
  type SyncRequestV1,
} from '@/contracts/offline-attendance-v1';
import {
  OFFLINE_SYNC_BACKOFF_BASE_MS,
  OFFLINE_SYNC_BACKOFF_MAX_MS,
  OFFLINE_SYNC_BATCH_MAX,
  OFFLINE_SYNC_LEASE_MS,
  OFFLINE_SYNC_LEASE_RENEW_MS,
  OFFLINE_SYNC_PAUSE_RECHECK_MS,
  OFFLINE_SYNC_PERIODIC_MS,
  clockTurnedBack,
  syncBackoffMs,
} from '@/contracts/offline-attendance-policy';
import { OFFLINE_RETRY_AFTER_MAX_MS, boundRetryAfterMs, type postSync } from '@/services/api/offline-attendance.api';
import { normalizeTenant } from '@/services/session-scope';
import {
  StorageError,
  acquireLease,
  applyResults,
  loadContext,
  loadGrantTokens,
  markInFlight,
  receiptAad,
  recoverInFlight,
  releaseLease,
  renewLease,
  requeueAll,
  selectBatch,
  unsealOutboxPayload,
  type AppliedResults,
  type LeaseAcquisition,
  type Requeue,
  type Settlement,
  type StorageErrorKind,
} from '@/services/storage/attendance-storage';
import type { DataKeyReady } from '@/services/storage/offline-keys';
import { SealedDataError, sealJson } from '@/services/storage/sealed';
import type {
  OfflineContextRecord,
  OfflineScope,
  OutboxRow,
  SealedEventPayload,
  SyncPauseReason,
  SyncPhase,
} from '@/types/offline-attendance';
import { IS_DEV } from '@/utils/env';
import { newUuidV4 } from '@/utils/uuid';

/* ── Public types ──────────────────────────────────────────────────────── */

/** What the engine reports after every change of what it is doing. */
export interface SyncPhaseReport {
  phase: SyncPhase;
  /** Why the engine is paused; `null` unless `phase === 'paused'`. */
  pauseReason: SyncPauseReason | null;
  /** When the queue head may be sent next (device ms), or `null`. */
  nextAttemptAt: number | null;
  /** The last time an upload settled at least one event (device ms), or `null`. */
  lastSuccessAt: number | null;
  /** The result code the queue head is waiting on (or the last failure's), `null` when there is none. */
  lastErrorCode: string | null;
}

export interface SyncEngineDeps {
  scope: OfflineScope;
  key: DataKeyReady;
  /** A FRESH id per engine instance (e.g. `newUuidV4()`): the lease fences engines by owner. */
  ownerId: string;
  /** Session epoch + orchestrator ownership guard: `false` once this engine's session is over. */
  isCurrent: () => boolean;
  api: { postSync: typeof postSync };
  /** Device ms. */
  now: () => number;
  /** A uniform draw in [0, 1] for the back-off jitter. */
  random: () => number;
  /** Events this engine just settled (the orchestrator refreshes the confirmed state and the store). */
  onSettled: (s: { accepted: string[]; rejected: string[] }) => void;
  onPhase: (p: SyncPhaseReport) => void;
}

/** The scope ERPat must echo for an answer to count (`employee_id` is checked only when given). */
export interface SettleScopeEcho {
  company_key: string;
  user_id: string;
  installation_id: string;
  employee_id?: string;
}

export type SettleOutcomeResult =
  | { usable: false; reason: string }
  | {
      usable: true;
      settlements: Array<{ clientEventId: string; receipt: FinalReceiptV1 }>;
      deferrals: Array<{ clientEventId: string; code: string; retryAfterMs: number | null }>;
      unresolved: string[];
    };

/* ── settleOutcome (pure) ──────────────────────────────────────────────── */

/**
 * What an answer to `sent` settles (D12). The response must decode, echo the
 * request's `sync_id` and the expected scope (company key case-insensitively;
 * `employee_id` only when the caller passes the stored context's). Then, per
 * SENT event:
 *   - a single decodable answer for it — repeats are fine when identical — is
 *     a settlement (accepted / rejected / duplicate) when its receipt names the
 *     event, its `event_type` and the same instant as the `occurred_at` sent, or
 *     a deferral (its `retry_after_ms` bounded at one hour, C16);
 *   - anything else — no answer, two different answers, a malformed entry
 *     naming it, a receipt for another action or time — leaves it UNRESOLVED.
 * Answers for ids that were not sent are ignored. Pure: no I/O, no clock.
 */
export function settleOutcome(sent: SyncRequestV1, response: unknown, scopeEcho: SettleScopeEcho): SettleOutcomeResult {
  const decoded = decodeSyncResponse(response);
  if (!decoded.ok) return { usable: false, reason: `response.${decoded.reason}` };
  const shell = decoded.value;
  if (shell.sync_id !== normalizeUuid(sent.sync_id)) return { usable: false, reason: 'sync_id' };
  if (normalizeTenant(shell.scope.company_key) !== normalizeTenant(scopeEcho.company_key)) {
    return { usable: false, reason: 'scope.company_key' };
  }
  if (shell.scope.user_id !== scopeEcho.user_id) return { usable: false, reason: 'scope.user_id' };
  if (shell.scope.installation_id !== normalizeUuid(scopeEcho.installation_id)) {
    return { usable: false, reason: 'scope.installation_id' };
  }
  if (scopeEcho.employee_id !== undefined && shell.scope.employee_id !== scopeEcho.employee_id) {
    return { usable: false, reason: 'scope.employee_id' };
  }

  const sentIds = new Set(sent.events.map((event) => event.client_event_id));
  const answers = new Map<string, { result: AttendanceResultV1; canonical: string }>();
  const contradicted = new Set<string>();
  for (const raw of shell.results) {
    const result = decodeResult(raw);
    if (!result.ok) {
      // A malformed entry that still names one of our events contradicts
      // whatever else is said about it.
      const named = isRecord(raw) ? normalizeUuid(raw.client_event_id) : null;
      if (named !== null && sentIds.has(named)) contradicted.add(named);
      continue;
    }
    const id = result.value.client_event_id;
    if (!sentIds.has(id)) continue;
    const canonical = stableJson(result.value);
    const earlier = answers.get(id);
    if (earlier === undefined) answers.set(id, { result: result.value, canonical });
    else if (earlier.canonical !== canonical) contradicted.add(id);
  }

  const settlements: Array<{ clientEventId: string; receipt: FinalReceiptV1 }> = [];
  const deferrals: Array<{ clientEventId: string; code: string; retryAfterMs: number | null }> = [];
  const unresolved: string[] = [];
  for (const event of sent.events) {
    const id = event.client_event_id;
    const answer = answers.get(id)?.result;
    if (answer === undefined || contradicted.has(id)) {
      unresolved.push(id);
    } else if (answer.status === 'deferred') {
      deferrals.push({ clientEventId: id, code: answer.code, retryAfterMs: boundRetryAfterMs(answer.retry_after_ms) });
    } else if (answer.receipt.event_type !== event.event_type || Date.parse(answer.receipt.occurred_at) !== Date.parse(event.occurred_at)) {
      unresolved.push(id); // a receipt for another punch than the one sent
    } else {
      settlements.push({ clientEventId: id, receipt: answer.receipt });
    }
  }
  return { usable: true, settlements, deferrals, unresolved };
}

/* ── The engine ────────────────────────────────────────────────────────── */

/** What a pass leaves for the loop: look again at once, rest until a wake, or end (stopped / not current). */
type PassResult = 'again' | 'rest' | 'end';

/** How a whole-request failure is handled. */
type FailurePlan =
  | { kind: 'backoff'; code: string; phase: 'offline' | 'waiting'; retryAfterMs: number | null }
  | { kind: 'rate_limited'; code: string; retryAfterMs: number | null }
  | { kind: 'pause'; reason: SyncPauseReason; code: string }
  | { kind: 'signed_out'; code: string };

/** The result code a requeued event carries when nothing more specific has copy. */
const UNRESOLVED = 'unresolved';
/** Codes the client records itself (each has copy in `offline-attendance.en.ts`). */
const CLIENT_CODES: ReadonlySet<string> = new Set([
  'network_error',
  'timeout',
  'security_challenge',
  'cancelled',
  'refresh_failed',
  'session_expired',
  'no_session',
  'invalid_response',
  UNRESOLVED,
]);

export class SyncEngine {
  private readonly deps: SyncEngineDeps;
  /** Bumped by `stop()`: work started under an older value settles nothing. */
  private generation = 0;
  private stopped = false;
  /** A loop is running (set synchronously, so a kick can never be lost between passes). */
  private active = false;
  private running: Promise<void> = Promise.resolve();
  private rerun = false;
  private forceNext = false;
  private pause: { reason: SyncPauseReason; until: number } | null = null;
  private wake: ReturnType<typeof setTimeout> | null = null;
  private renewal: ReturnType<typeof setInterval> | null = null;
  /** The lease generation the current pass holds, or `null`. */
  private held: number | null = null;
  private request: AbortController | null = null;
  private lastSuccessAt: number | null = null;
  private lastErrorCode: string | null = null;

  constructor(deps: SyncEngineDeps) {
    this.deps = deps;
  }

  /** Ask for a pass (single flight: during one, the request becomes a re-run). `force` ignores the head's back-off and a pause. */
  kick(opts?: { force?: boolean }): void {
    if (this.stopped) return;
    if (opts?.force === true) this.forceNext = true;
    void this.run();
  }

  /** Run passes until nothing more is due; joins (and re-runs) a loop already running. Never rejects. */
  run(): Promise<void> {
    if (this.stopped) return Promise.resolve();
    if (this.active) {
      this.rerun = true;
      return this.running;
    }
    this.active = true;
    this.running = this.loop(this.generation);
    return this.running;
  }

  /**
   * End this engine for good (sign-out, company switch, a new session): bump
   * the generation, clear the timers, abort the request, give the lease up
   * (best effort). In-flight rows are left in flight — the next owner recovers
   * them; a late answer settles nothing.
   */
  async stop(): Promise<void> {
    if (this.stopped) return;
    this.stopped = true;
    this.generation += 1;
    this.clearWake();
    this.stopRenewal();
    this.request?.abort();
    this.request = null;
    const held = this.held;
    this.held = null;
    if (held !== null) await this.release(held);
  }

  /** Clear a pause (after a successful provisioning or sign-in) and look again. */
  resume(): void {
    if (this.stopped) return;
    this.pause = null;
    this.kick();
  }

  /* ── The loop ────────────────────────────────────────────────────────── */

  private async loop(gen: number): Promise<void> {
    try {
      for (;;) {
        this.rerun = false;
        const force = this.forceNext;
        this.forceNext = false;
        const result = await this.pass(gen, force);
        if (result === 'end' || !this.alive(gen)) break;
        if (result === 'again') continue;
        if (!this.rerun) break;
      }
    } catch (err) {
      // A bug, not a sync outcome: the rows are untouched or recoverable, so
      // try again after the base back-off rather than stop syncing for good.
      if (IS_DEV) console.warn('[offline-sync] unexpected failure in a sync pass', errorName(err));
      if (this.alive(gen)) this.scheduleAt(this.deps.now() + OFFLINE_SYNC_BACKOFF_BASE_MS, true);
    } finally {
      // Synchronously with the last check of `rerun`: a kick from now on starts a new loop.
      this.active = false;
    }
  }

  /** One pass: take the lease, then do the owned work; the lease is given up at the end. */
  private async pass(gen: number, force: boolean): Promise<PassResult> {
    if (!this.alive(gen)) return 'end';
    const { scope, ownerId } = this.deps;
    const startedAt = this.deps.now();

    if (this.pause !== null) {
      // A pause further ahead than the 15 minutes it ever lasts was set before the clock was turned back: re-check now.
      if (!force && startedAt < this.pause.until && !clockTurnedBack(this.pause.until, startedAt, OFFLINE_SYNC_PAUSE_RECHECK_MS)) {
        this.emit('paused', null);
        this.scheduleAt(this.pause.until, false);
        return 'rest';
      }
      this.pause = null; // re-check: the failure, if it is still there, pauses again
    }

    let lease: LeaseAcquisition;
    try {
      lease = await acquireLease(scope.scopeKey, ownerId, startedAt);
    } catch (err) {
      // IndexedDB cannot even hold the lease: nothing can be settled here.
      if (!this.alive(gen)) return 'end';
      return this.enterPause(storagePauseReason(err), null);
    }
    if (!lease.held) {
      if (!this.alive(gen)) return 'end';
      this.lastErrorCode = null;
      this.emit('waiting', lease.expiresAt);
      this.scheduleAt(lease.expiresAt, true);
      return 'rest';
    }
    const leaseGen = lease.generation;
    if (!this.alive(gen)) {
      await this.release(leaseGen);
      return 'end';
    }
    this.held = leaseGen;
    try {
      return await this.ownedPass(gen, force, leaseGen);
    } finally {
      // Give the lease up unless it was lost, or stop() already did.
      if (this.held === leaseGen) {
        this.held = null;
        await this.release(leaseGen);
      }
    }
  }

  /** The work done under the lease: recover, select, freeze the request, send, settle. */
  private async ownedPass(gen: number, force: boolean, leaseGen: number): Promise<PassResult> {
    const { scope, key, ownerId } = this.deps;
    const startedAt = this.deps.now();

    try {
      await recoverInFlight(scope.scopeKey, ownerId, leaseGen, startedAt);
    } catch (err) {
      // Lost the lease (C15), or IndexedDB failed.
      if (!this.alive(gen)) return 'end';
      return isStorageKind(err, 'not_owner') ? this.lostLease() : this.enterPause(storagePauseReason(err), null);
    }
    if (!this.alive(gen)) return 'end';

    let context: OfflineContextRecord | null;
    try {
      context = await loadContext(key, scope.scopeKey);
    } catch (err) {
      // The stored context does not open (key) or IndexedDB failed (storage).
      if (!this.alive(gen)) return 'end';
      return this.enterPause(storagePauseReason(err), null);
    }
    if (!this.alive(gen)) return 'end';

    let batch: OutboxRow[];
    try {
      // MAX_SAFE_INTEGER as "now": the batch the head governs, due or not — its due time is checked here.
      batch = await selectBatch(scope.scopeKey, Number.MAX_SAFE_INTEGER, batchLimit(context));
    } catch (err) {
      // IndexedDB failed while selecting.
      if (!this.alive(gen)) return 'end';
      return this.enterPause(storagePauseReason(err), null);
    }
    if (!this.alive(gen)) return 'end';

    const head = batch[0];
    if (head === undefined) {
      this.lastErrorCode = null;
      this.clearWake();
      this.emit('idle', null);
      return 'rest';
    }
    if (!force && !headDue(head, startedAt)) {
      this.lastErrorCode = head.resultCode;
      this.emit('waiting', head.nextAttemptAt);
      this.scheduleAt(head.nextAttemptAt, true);
      return 'rest';
    }

    // Freeze the request from the sealed payloads — exactly what was captured.
    const events: EmployeeAttendanceEventV1[] = [];
    try {
      for (const row of batch) events.push(wireEvent(row, await unsealOutboxPayload(key, row)));
    } catch {
      // A queued punch does not open under this key: never send part of a chain.
      if (!this.alive(gen)) return 'end';
      return this.enterPause('key', null);
    }
    let grants: Record<string, string>;
    try {
      grants = await loadGrantTokens(key, scope.scopeKey, distinct(batch.map((row) => row.grantId)));
    } catch (err) {
      // An archived grant does not open (key) or IndexedDB failed (storage).
      if (!this.alive(gen)) return 'end';
      return this.enterPause(storagePauseReason(err), null);
    }
    if (!this.alive(gen)) return 'end';
    if (batch.some((row) => grants[row.grantId] === undefined)) return this.enterPause('storage', null);

    const ids = batch.map((row) => row.clientEventId);
    try {
      await markInFlight(scope.scopeKey, ownerId, leaseGen, ids, startedAt);
    } catch (err) {
      // C15: lost the lease, or a row is no longer pending — end quietly and select afresh later.
      if (!this.alive(gen)) return 'end';
      if (isStorageKind(err, 'not_owner')) return this.lostLease();
      if (isStorageKind(err, 'state_changed')) {
        this.emit('waiting', startedAt + OFFLINE_SYNC_BACKOFF_BASE_MS);
        this.scheduleAt(startedAt + OFFLINE_SYNC_BACKOFF_BASE_MS, true);
        return 'rest';
      }
      return this.enterPause(storagePauseReason(err), null);
    }
    if (!this.alive(gen)) return 'end'; // rows stay in flight: the next owner recovers them

    const body: SyncRequestV1 = {
      schema_version: OFFLINE_SCHEMA_VERSION,
      sync_id: newUuidV4(),
      installation_id: scope.installationId,
      grants,
      evidence_refs: {},
      events,
    };
    this.emit('syncing', null);
    const controller = new AbortController();
    this.request = controller;
    this.startRenewal(gen, leaseGen);
    let response: unknown;
    try {
      response = await this.deps.api.postSync(body, controller.signal);
    } catch (err) {
      // No usable answer: classified below; the rows are in flight under this lease.
      if (!this.alive(gen)) return 'end';
      return this.afterFailure(gen, leaseGen, head, ids, err);
    } finally {
      this.stopRenewal();
      if (this.request === controller) this.request = null;
    }
    if (!this.alive(gen)) return 'end'; // a late answer (sign-out, switch, stop): settles nothing
    return this.settle(gen, leaseGen, body, batch, response, context);
  }

  /* ── Settling an answer ──────────────────────────────────────────────── */

  private async settle(
    gen: number,
    leaseGen: number,
    body: SyncRequestV1,
    batch: OutboxRow[],
    response: unknown,
    context: OfflineContextRecord | null,
  ): Promise<PassResult> {
    const { scope, key, ownerId } = this.deps;
    const ids = batch.map((row) => row.clientEventId);
    const rowOf = new Map(batch.map((row) => [row.clientEventId, row]));
    const now = this.deps.now();

    const outcome = settleOutcome(body, response, {
      company_key: scope.companyKey,
      user_id: scope.userId,
      installation_id: scope.installationId,
      employee_id: expectedEmployeeId(context, scope),
    });
    if (!outcome.usable) {
      if (IS_DEV) console.warn('[offline-sync] the sync answer was not usable', outcome.reason);
      return this.requeueOrPause(gen, leaseGen, ids, now + this.backoff(batch[0]), UNRESOLVED);
    }

    let settlements: Settlement[];
    try {
      // Sealed BEFORE the settlement transaction (only Dexie is awaited inside one).
      settlements = await Promise.all(
        outcome.settlements.map(async ({ clientEventId, receipt }) => ({
          clientEventId,
          outcome: receipt.outcome,
          code: receipt.code,
          receivedAtMs: Date.parse(receipt.received_at),
          attendanceId: receipt.attendance_id,
          sealedReceipt: await sealJson(key, receiptAad(scope.scopeKey, clientEventId), receipt),
        })),
      );
    } catch (err) {
      // The key cannot seal: put the batch back (same ids next time) and pause on the key.
      if (!this.alive(gen)) return 'end';
      const back = await this.requeueOrPause(gen, leaseGen, ids, now + OFFLINE_SYNC_BACKOFF_BASE_MS, UNRESOLVED);
      // Only once the rows are back: a lost lease stays quiet (C15), a failed requeue already paused.
      return back === 'again' ? this.enterPause('key', null) : back;
    }
    if (!this.alive(gen)) return 'end';

    const requeues: Requeue[] = [
      ...outcome.deferrals.map((deferral) => ({
        clientEventId: deferral.clientEventId,
        resultCode: deferral.code,
        nextAttemptAt: now + Math.max(deferral.retryAfterMs ?? 0, this.backoff(rowOf.get(deferral.clientEventId))),
      })),
      ...outcome.unresolved.map((clientEventId) => ({
        clientEventId,
        resultCode: UNRESOLVED,
        nextAttemptAt: now + this.backoff(rowOf.get(clientEventId)),
      })),
    ];

    let applied: AppliedResults;
    try {
      applied = await applyResults(scope.scopeKey, ownerId, leaseGen, settlements, requeues, now);
    } catch (err) {
      // C15: a lost lease settles nothing and pauses nothing.
      if (!this.alive(gen)) return 'end';
      if (isStorageKind(err, 'not_owner')) return this.lostLease();
      // The settlement could not be stored: send the same ids again.
      return this.requeueOrPause(gen, leaseGen, ids, now + OFFLINE_SYNC_BACKOFF_BASE_MS, UNRESOLVED);
    }
    if (!this.alive(gen)) return 'end';

    const settledIds = new Set(applied.settled);
    const accepted = settlements.filter((s) => s.outcome === 'accepted' && settledIds.has(s.clientEventId)).map((s) => s.clientEventId);
    const rejected = settlements.filter((s) => s.outcome === 'rejected' && settledIds.has(s.clientEventId)).map((s) => s.clientEventId);
    if (accepted.length + rejected.length > 0) {
      this.lastSuccessAt = now;
      this.notifySettled({ accepted, rejected });
    }
    // Look again: more may be due now, and the next pass reports what waits and why.
    return 'again';
  }

  /* ── Whole-request failures ──────────────────────────────────────────── */

  private async afterFailure(gen: number, leaseGen: number, head: OutboxRow, ids: string[], err: unknown): Promise<PassResult> {
    const plan = classifyFailure(err);
    const now = this.deps.now();
    switch (plan.kind) {
      case 'signed_out':
        // No session to send with: leave the rows in flight (recovered by the
        // next owner, after sign-in) and pause until then.
        return this.enterPause('signed_out', plan.code);
      case 'pause': {
        // The rows go back due at once: the pause, not a back-off, decides when they are sent.
        const back = await this.requeueOrPause(gen, leaseGen, ids, now, plan.code);
        // Only once the rows are back: a lost lease stays quiet (C15), a failed requeue already paused.
        return back === 'again' ? this.enterPause(plan.reason, plan.code) : back;
      }
      case 'rate_limited': {
        const at = now + Math.max(plan.retryAfterMs ?? 0, OFFLINE_SYNC_BACKOFF_BASE_MS);
        return this.requeueAndWait(gen, leaseGen, ids, at, plan.code, 'waiting');
      }
      case 'backoff': {
        const at = now + Math.max(this.backoff(head), plan.retryAfterMs ?? 0);
        return this.requeueAndWait(gen, leaseGen, ids, at, plan.code, plan.phase);
      }
      default: {
        const exhaustive: never = plan;
        return exhaustive;
      }
    }
  }

  /** Put the batch back due at `at` and report `phase` until then. */
  private async requeueAndWait(
    gen: number,
    leaseGen: number,
    ids: string[],
    at: number,
    code: string,
    phase: 'offline' | 'waiting',
  ): Promise<PassResult> {
    const back = await this.requeueOrPause(gen, leaseGen, ids, at, code);
    if (back !== 'again') return back;
    this.lastErrorCode = code;
    this.emit(phase, at);
    this.scheduleAt(at, true);
    return 'rest';
  }

  /**
   * Return the caller's in-flight rows to `pending` (due `at`, with `code`).
   * `'again'` when done; a lost lease ends quietly (C15); any other failure
   * leaves the rows in flight (recovered next run) and pauses on storage.
   */
  private async requeueOrPause(gen: number, leaseGen: number, ids: string[], at: number, code: string): Promise<PassResult> {
    const { scope, ownerId } = this.deps;
    try {
      await requeueAll(scope.scopeKey, ownerId, leaseGen, ids, at, resultCodeFor(code));
    } catch (err) {
      // Lost the lease (quiet), or IndexedDB failed (the rows stay in flight).
      if (!this.alive(gen)) return 'end';
      return isStorageKind(err, 'not_owner') ? this.lostLease() : this.enterPause(storagePauseReason(err), code);
    }
    return this.alive(gen) ? 'again' : 'end';
  }

  /** C15: another owner holds the scope now — it recovers and resends; look again once a lease could have lapsed. */
  private lostLease(): PassResult {
    this.held = null;
    const at = this.deps.now() + OFFLINE_SYNC_LEASE_MS;
    this.emit('waiting', at);
    this.scheduleAt(at, true);
    return 'rest';
  }

  private enterPause(reason: SyncPauseReason, code: string | null): PassResult {
    const until = this.deps.now() + OFFLINE_SYNC_PAUSE_RECHECK_MS;
    this.pause = { reason, until };
    if (code !== null) this.lastErrorCode = code;
    this.emit('paused', null);
    this.scheduleAt(until, false);
    return 'rest';
  }

  /** The jittered wait before the next try of `row` (`syncBackoffMs` of its attempts so far; no cap on attempts). */
  private backoff(row: OutboxRow | undefined): number {
    const attempts = row !== undefined && Number.isSafeInteger(row.attempts) && row.attempts >= 0 ? row.attempts : 0;
    return syncBackoffMs(attempts, this.deps.random);
  }

  /* ── Lease renewal while a request is out ────────────────────────────── */

  private startRenewal(gen: number, leaseGen: number): void {
    this.stopRenewal();
    this.renewal = setInterval(() => {
      void this.renew(gen, leaseGen);
    }, OFFLINE_SYNC_LEASE_RENEW_MS);
  }

  private stopRenewal(): void {
    if (this.renewal !== null) clearInterval(this.renewal);
    this.renewal = null;
  }

  /**
   * Keep the lease live while the request is out (it may take longer than the
   * lease lasts). A lapsed lease nobody took over is re-acquired with the same
   * generation; one taken over stays lost (the settlement will be `not_owner`).
   */
  private async renew(gen: number, leaseGen: number): Promise<void> {
    if (!this.alive(gen) || this.held !== leaseGen) return;
    const { scope, ownerId } = this.deps;
    try {
      if (await renewLease(scope.scopeKey, ownerId, leaseGen, this.deps.now())) return;
      const again = await acquireLease(scope.scopeKey, ownerId, this.deps.now());
      // Released and re-taken under a new generation: not this batch's — give it back.
      if (again.held && again.generation !== leaseGen) await this.release(again.generation);
    } catch (err) {
      // Best effort: the fence at settlement decides.
      if (IS_DEV) console.warn('[offline-sync] could not renew the sync lease', errorName(err));
    }
  }

  private async release(leaseGen: number): Promise<void> {
    try {
      await releaseLease(this.deps.scope.scopeKey, this.deps.ownerId, leaseGen);
    } catch (err) {
      // Best effort: an unreleased lease simply expires after OFFLINE_SYNC_LEASE_MS.
      if (IS_DEV) console.warn('[offline-sync] could not release the sync lease', errorName(err));
    }
  }

  /* ── Plumbing ────────────────────────────────────────────────────────── */

  private alive(gen: number): boolean {
    if (this.stopped || gen !== this.generation) return false;
    try {
      return this.deps.isCurrent();
    } catch (err) {
      // A guard that cannot answer is a session that is not current.
      if (IS_DEV) console.warn('[offline-sync] isCurrent() failed', errorName(err));
      return false;
    }
  }

  /** Wake for another pass at `at` — bounded by the periodic wake unless it is a pause re-check. */
  private scheduleAt(at: number, bounded: boolean): void {
    if (this.stopped) return;
    this.clearWake();
    const now = this.deps.now();
    let delay = Number.isFinite(at) ? Math.max(0, at - now) : OFFLINE_SYNC_PERIODIC_MS;
    if (bounded) delay = Math.min(delay, OFFLINE_SYNC_PERIODIC_MS);
    this.wake = setTimeout(() => {
      this.wake = null;
      this.kick();
    }, delay);
  }

  private clearWake(): void {
    if (this.wake !== null) clearTimeout(this.wake);
    this.wake = null;
  }

  private emit(phase: SyncPhase, nextAttemptAt: number | null): void {
    if (this.stopped) return;
    const report: SyncPhaseReport = {
      phase,
      pauseReason: phase === 'paused' ? (this.pause?.reason ?? null) : null,
      nextAttemptAt,
      lastSuccessAt: this.lastSuccessAt,
      lastErrorCode: this.lastErrorCode,
    };
    try {
      this.deps.onPhase(report);
    } catch (err) {
      // A listener's bug must not break syncing.
      if (IS_DEV) console.warn('[offline-sync] onPhase listener failed', errorName(err));
    }
  }

  private notifySettled(s: { accepted: string[]; rejected: string[] }): void {
    try {
      this.deps.onSettled(s);
    } catch (err) {
      // A listener's bug must not undo or repeat a stored settlement.
      if (IS_DEV) console.warn('[offline-sync] onSettled listener failed', errorName(err));
    }
  }
}

/* ── Helpers ───────────────────────────────────────────────────────────── */

/**
 * The longest wait the engine ever puts before a SENT event: a retry hint
 * (`Retry-After`, a deferral's `retry_after_ms` — bounded at one hour, C16)
 * or the back-off ceiling. No requeue waits longer than the larger of the two,
 * so their sum is a safe upper bound; `clockTurnedBack` adds its own slack.
 */
const LONGEST_RETRY_WAIT_MS = OFFLINE_RETRY_AFTER_MAX_MS + OFFLINE_SYNC_BACKOFF_MAX_MS;

/**
 * Whether the queue head may be sent at `now` (device wall clock). It is due
 * when its `nextAttemptAt` has come — and also when that time could only have
 * been reached by a clock turned back since it was stored:
 *   - a punch never sent (`attempts === 0`) was due the moment it was saved
 *     (`nextAttemptAt` = its capture time); nothing ever scheduled it later, so
 *     a capture time "ahead" of now is a clock set right after a fast capture
 *     — even by minutes — and it is due;
 *   - a sent one waits at most {@link LONGEST_RETRY_WAIT_MS}; a retry time
 *     further ahead than that is a clock turned back (`clockTurnedBack`), and
 *     waiting it out would hold the queue for as long as the clock moved.
 * A wait within the bound — the server's Retry-After included — is honoured.
 * Sending early never risks a second attendance row: the event goes again
 * with the same `client_event_id`, and ERPat answers a repeat `duplicate`.
 */
function headDue(head: OutboxRow, now: number): boolean {
  if (head.nextAttemptAt <= now || head.attempts === 0) return true;
  return clockTurnedBack(head.nextAttemptAt, now, LONGEST_RETRY_WAIT_MS);
}

/** The wire event of an outbox row: its sealed payload as frozen at capture, plus the row's id and device sequence. */
function wireEvent(row: OutboxRow, payload: SealedEventPayload): EmployeeAttendanceEventV1 {
  return {
    client_event_id: row.clientEventId,
    event_type: payload.event_type,
    occurred_at: payload.occurred_at,
    device_occurred_at: payload.device_occurred_at,
    device_seq: row.deviceSeq,
    timezone: payload.timezone,
    clock: { offset_ms: payload.clock.offset_ms, calibrated_at: payload.clock.calibrated_at, uncertainty_ms: payload.clock.uncertainty_ms },
    grant_id: payload.grant_id,
    policy_version: payload.policy_version,
    base: {
      attendance_id: payload.base.attendance_id,
      attendance_revision: payload.base.attendance_revision,
      local_shift_id: payload.base.local_shift_id,
      expected_state: payload.base.expected_state,
      expected_break_count: payload.base.expected_break_count,
    },
    depends_on_event_id: payload.depends_on_event_id,
    ...(typeof payload.note === 'string' ? { note: payload.note } : {}),
    captured_connectivity: payload.captured_connectivity,
    app_version: payload.app_version,
  };
}

/** The most events one request may carry: 50, or the policy's smaller maximum (C9). */
function batchLimit(context: OfflineContextRecord | null): number {
  const fromPolicy = context?.policy?.max_batch_events;
  return typeof fromPolicy === 'number' && Number.isSafeInteger(fromPolicy) && fromPolicy >= 1
    ? Math.min(OFFLINE_SYNC_BATCH_MAX, fromPolicy)
    : OFFLINE_SYNC_BATCH_MAX;
}

/** The employee ERPat must echo: the stored context's (pinned at provisioning), else the user id (v1: `eid === uid`). */
function expectedEmployeeId(context: OfflineContextRecord | null, scope: OfflineScope): string {
  return context !== null && context.scope.scopeKey === scope.scopeKey ? context.employeeId : scope.userId;
}

/**
 * How a failed request is handled. ERPat's envelope codes first, then the HTTP
 * status: no answer or a server error backs off (no attempt cap); a refused
 * session leaves the rows for the next owner; permission, account, module,
 * schema and protocol refusals pause.
 */
function classifyFailure(err: unknown): FailurePlan {
  if (!isOfflineApiError(err)) return { kind: 'backoff', code: 'network_error', phase: 'offline', retryAfterMs: null };
  const { code, status, retryAfterMs } = err;
  switch (code) {
    case 'session_expired':
    case 'no_session':
      return { kind: 'signed_out', code };
    case 'forbidden':
      return { kind: 'pause', reason: 'permission', code };
    case 'account_inactive':
      return { kind: 'pause', reason: 'account_inactive', code };
    case 'module_unavailable':
      return { kind: 'pause', reason: 'module_unavailable', code };
    case 'unsupported_schema':
      return { kind: 'pause', reason: 'unsupported', code };
    case 'validation_failed':
      return { kind: 'pause', reason: 'protocol_error', code };
    case 'rate_limited':
      return { kind: 'rate_limited', code, retryAfterMs };
    case 'offline_sync_unavailable':
      return { kind: 'backoff', code, phase: 'waiting', retryAfterMs };
    case 'network_error':
    case 'timeout':
    case 'refresh_failed':
      return { kind: 'backoff', code, phase: 'offline', retryAfterMs };
    case 'security_challenge':
    case 'cancelled':
      return { kind: 'backoff', code, phase: 'waiting', retryAfterMs };
    default:
      break;
  }
  if (status === 401) return { kind: 'signed_out', code: 'session_expired' };
  if (status === 429) return { kind: 'rate_limited', code: 'rate_limited', retryAfterMs };
  if (status === 0) return { kind: 'backoff', code: 'network_error', phase: 'offline', retryAfterMs };
  if (status >= 500) {
    const known = code === 'invalid_response' || DEFERRAL_CODES.includes(code) ? code : 'server_error';
    return { kind: 'backoff', code: known, phase: 'waiting', retryAfterMs };
  }
  if (status === 404) return { kind: 'pause', reason: 'unsupported', code: 'invalid_response' };
  // A 403 that is not ERPat's own `forbidden` (a web page, a bare status) is no verdict on the account.
  if (status === 403 || status === 408) return { kind: 'backoff', code: 'invalid_response', phase: 'waiting', retryAfterMs };
  if (status >= 400) return { kind: 'pause', reason: 'protocol_error', code: resultCodeFor(code) };
  // A 2xx/3xx that is not the envelope: a captive portal, the /install redirect.
  return { kind: 'backoff', code: 'invalid_response', phase: 'waiting', retryAfterMs };
}

/**
 * A result code a pending row may carry: one the copy knows (a client code, a
 * deferral, a whole-request error) — anything else, e.g. `http_400`, reads
 * `invalid_response`, so a waiting punch never shows the "Ask HR" fallback.
 */
function resultCodeFor(code: string): string {
  return CLIENT_CODES.has(code) || DEFERRAL_CODES.includes(code) || WHOLE_REQUEST_ERROR_CODES.includes(code)
    ? code
    : 'invalid_response';
}

/** A transport failure from `offline-attendance.api.ts` (by class, or by shape across module instances). */
function isOfflineApiError(err: unknown): err is { code: string; status: number; retryAfterMs: number | null } {
  if (!isRecord(err)) return false;
  return (
    Reflect.get(err, 'name') === 'OfflineApiError' &&
    typeof Reflect.get(err, 'code') === 'string' &&
    typeof Reflect.get(err, 'status') === 'number' &&
    (Reflect.get(err, 'retryAfterMs') === null || typeof Reflect.get(err, 'retryAfterMs') === 'number')
  );
}

function storageKindOf(err: unknown): StorageErrorKind | null {
  if (err instanceof StorageError) return err.kind;
  if (isRecord(err) && Reflect.get(err, 'name') === 'StorageError') {
    const kind: unknown = Reflect.get(err, 'kind');
    return typeof kind === 'string' ? (kind as StorageErrorKind) : null;
  }
  return null;
}

function isStorageKind(err: unknown, kind: StorageErrorKind): boolean {
  return storageKindOf(err) === kind;
}

/** Data that does not open under the key pauses on `key`; any other storage failure on `storage`. */
function storagePauseReason(err: unknown): SyncPauseReason {
  if (storageKindOf(err) === 'corrupt' && isRecord(err) && Reflect.get(err, 'cause') instanceof SealedDataError) return 'key';
  return 'storage';
}

function distinct(values: readonly string[]): string[] {
  return [...new Set(values)];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** JSON with object keys sorted — two decoded answers are "identical" when this matches. */
function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (isRecord(value)) {
    const keys = Object.keys(value).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableJson(value[k])}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

function errorName(err: unknown): string {
  if (isRecord(err)) {
    const name: unknown = Reflect.get(err, 'name');
    if (typeof name === 'string' && name.length > 0) return name;
  }
  return 'UnknownError';
}
